import React from 'react';

import type { EventType, EventVisibility } from '../../api/types';
import type { DashboardFilters, PeriodFilter } from '../../domain/events/dashboard';

import './DashboardFilters.css';

type DashboardFiltersProps = {
  filters: DashboardFilters;
  onChange: (filters: DashboardFilters) => void;
  showCreatorFilter: boolean;
};

function toggleSet<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
}

export function DashboardFiltersPanel({ filters, onChange, showCreatorFilter }: DashboardFiltersProps) {
  const setGameType = (type: EventType) => {
    onChange({ ...filters, gameTypes: toggleSet(filters.gameTypes, type) });
  };

  const setParticipation = (value: 'participating' | 'invited' | 'none') => {
    onChange({ ...filters, participation: toggleSet(filters.participation, value) });
  };

  const setCreator = (value: 'mine' | 'others') => {
    onChange({ ...filters, creator: toggleSet(filters.creator, value) });
  };

  const setPeriod = (period: PeriodFilter) => {
    onChange({ ...filters, period });
  };

  const setVisibility = (value: EventVisibility) => {
    onChange({ ...filters, visibility: toggleSet(filters.visibility, value) });
  };

  const setAvailability = (value: 'hasSlots' | 'full') => {
    onChange({ ...filters, availability: toggleSet(filters.availability, value) });
  };

  return (
    <div className="dashboard-filters">

      {/* Group 1: Game type */}
      <div className="filter-chips">
        <button
          type="button"
          className={`filter-chip${filters.gameTypes.has('boardgames') ? ' filter-chip--active' : ''}`}
          onClick={() => setGameType('boardgames')}
        >
          🎲 Настольные
        </button>
        <button
          type="button"
          className={`filter-chip${filters.gameTypes.has('ping_pong') ? ' filter-chip--active' : ''}`}
          onClick={() => setGameType('ping_pong')}
        >
          🏓 Теннис
        </button>
        <button
          type="button"
          className={`filter-chip${filters.gameTypes.has('fencing') ? ' filter-chip--active' : ''}`}
          onClick={() => setGameType('fencing')}
        >
          ⚔️ Фехт.
        </button>
        <button
          type="button"
          className={`filter-chip${filters.gameTypes.has('archery') ? ' filter-chip--active' : ''}`}
          onClick={() => setGameType('archery')}
        >
          🏹 Лук
        </button>
        <button
          type="button"
          className={`filter-chip${filters.gameTypes.has('hiking') ? ' filter-chip--active' : ''}`}
          onClick={() => setGameType('hiking')}
        >
          🌲 Походы
        </button>
        <button
          type="button"
          className={`filter-chip${filters.gameTypes.has('courtyard') ? ' filter-chip--active' : ''}`}
          onClick={() => setGameType('courtyard')}
        >
          🏡 Двор
        </button>
      </div>

      {/* Group 2: Participation */}
      <div className="filter-chips">
        <button
          type="button"
          className={`filter-chip${filters.participation.has('participating') ? ' filter-chip--active filter-chip--active-green' : ''}`}
          onClick={() => setParticipation('participating')}
        >
          🟢 Участвую
        </button>
        <button
          type="button"
          className={`filter-chip${filters.participation.has('invited') ? ' filter-chip--active filter-chip--active-yellow' : ''}`}
          onClick={() => setParticipation('invited')}
        >
          ⏳ Пригл.
        </button>
        <button
          type="button"
          className={`filter-chip${filters.participation.has('none') ? ' filter-chip--active filter-chip--active-red' : ''}`}
          onClick={() => setParticipation('none')}
        >
          🚷 Не участвую
        </button>
      </div>

      {/* Group 3: Creator (org/admin only) */}
      {showCreatorFilter && (
        <div className="filter-chips">
          <button
            type="button"
            className={`filter-chip${filters.creator.has('mine') ? ' filter-chip--active' : ''}`}
            onClick={() => setCreator('mine')}
          >
            👑 Мои игры
          </button>
          <button
            type="button"
            className={`filter-chip${filters.creator.has('others') ? ' filter-chip--active' : ''}`}
            onClick={() => setCreator('others')}
          >
            👥 Чужие игры
          </button>
        </div>
      )}

      {/* Groups 4+5: Visibility and Availability — same row with divider */}
      <div className="filter-row-double">
        <div className="filter-chips">
          <button
            type="button"
            className={`filter-chip${filters.visibility.has('open') ? ' filter-chip--active' : ''}`}
            onClick={() => setVisibility('open')}
          >
            🌐
          </button>
          <button
            type="button"
            className={`filter-chip${filters.visibility.has('private') ? ' filter-chip--active' : ''}`}
            onClick={() => setVisibility('private')}
          >
            🔒
          </button>
        </div>

        <div className="filter-row-divider" />

        <div className="filter-chips">
          <button
            type="button"
            className={`filter-chip${filters.availability.has('hasSlots') ? ' filter-chip--active filter-chip--active-green' : ''}`}
            onClick={() => setAvailability('hasSlots')}
          >
            🟢 Есть Места
          </button>
          <button
            type="button"
            className={`filter-chip${filters.availability.has('full') ? ' filter-chip--active filter-chip--active-red' : ''}`}
            onClick={() => setAvailability('full')}
          >
            🔴 Нет Мест
          </button>
        </div>
      </div>

      {/* Group 6: Period (single-select, last) */}
      <div className="filter-chips">
        {([
          ['today', 'Сегодня'],
          ['1week', '1 нед.'],
          ['2weeks', '2 нед.'],
          ['1month', '1 мес.'],
          ['2months', '2 мес.'],
          ['3months', '3 мес.'],
        ] as [PeriodFilter, string][]).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`filter-chip${filters.period === value ? ' filter-chip--active' : ''}`}
            onClick={() => setPeriod(value)}
          >
            {label}
          </button>
        ))}
      </div>

    </div>
  );
}
